import React, { useCallback, useContext, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Position } from '@blueprintjs/core';
import { Box, Button, Flex, Text, Tooltip } from 'core/components';
import { defaultOptionRenderer } from 'core/components/select/SelectPopover';
import { Field, InputGroup, Select } from 'core/form';
import VariableTag from 'app/views/boards/VariableTag';
import {
  buildBidirectionalKey,
  getDefaultOperatorForDimension,
  isAgentDimension,
  isContainsDimension,
  isLabelDimension,
  isTestDimension,
  splitBidirectionalKey
} from '@kentik/ui-shared/query/dimensionUtils';
import { checkMeasurement, getMeasurementItem } from '@kentik/ui-shared/query/measurements';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { isSrcDim, isDstDim } from '@kentik/ui-shared/query/query';
import { getOperatorsForMeasurementDimension } from '@kentik/ui-shared/dictionary/v2/compilers/operators';
import { CUSTOM_FILTER_REGEX } from '@kentik/ui-shared/util/regex';
import { LookupStore } from 'app/stores/$lookups';
import { SynStore } from 'app/stores/synthetics/synthTypes';
import type { Dictionary } from 'app/stores/$dictionary';
import { MeasurementHash } from 'app/stores/dictionary/useDictionaryMeasurements';
import { getDimensionLabel, getGroupedDimensions, markSelectedDimension } from 'app/stores/ude/measurementTransforms';
import DropdownMenu from 'core/components/DropdownMenu';
import DimensionMenu from 'app/views/boards/panels/ude/components/DimensionMenu';
import { LabelStore } from 'app/stores/label/$labels';
import { intOperators } from '@kentik/ui-shared/dictionary/operators';
import { isEnumItem } from '@kentik/ui-shared/query/enumUtils';
import { FilterState } from './filter.types';
import AgentFilterInput from './synthetics/AgentFilterInput';
import TestFilterInput from './synthetics/TestFilterInput';
import LabelFilterInput from './LabelFilterInput';
import BoardVariablesContext from './BoardVariablesContext';
import { isFieldComparisonOperator } from './filterConstants';

// Minimal interface for Dictionary properties we need
interface DictionaryLike {
  filterOperatorOptions?: Record<string, Array<{ value: string; label: string }>>;
  standardFilterOperators?: Array<{ value: string; label: string }>;
  stringOperators?: Array<{ value: string; label: string }>;
  filterFieldValueOptions?: Record<string, Array<{ value: string; label: string }>>;
  rightFilterFieldOptions?: Record<string, Array<{ value: string; label: string }>>;
  getFilterValueValidator?: (dimensionName: string) => unknown;
  dictionary?: {
    queryFilters?: {
      nms?: {
        operatorOptions?: Record<string, Array<{ value: string; label: string }>>;
        standardOperatorOptions?: Array<{ value: string; label: string }>;
      };
      suppressValueOperators?: string[];
    };
  };
}

interface UdeFilterItemProps {
  filter: FilterState;
  index: number;
  onRemove: (index: number) => void;
  measurement: string | null;
  measurements: Measurement[];
  measurementHash: MeasurementHash;
  autoFocus?: boolean;
  $lookups?: LookupStore;
  $syn?: SynStore;
  $dictionary?: Dictionary;
  $labels?: LabelStore;
}

function getOperatorOptions(
  dimensionName: string,
  measurement: string | null,
  $dictionary?: Dictionary,
  measurements?: Measurement[]
): { value: string; label: string }[] {
  const dict = ($dictionary || {}) as DictionaryLike;

  // All label pseudo-dimensions (ktlabel_syn_agent, ktlabel_syn_test, i_device_label) use the same operators.
  if (isLabelDimension(dimensionName)) {
    return [
      { value: '=', label: 'includes' },
      { value: '<>', label: 'excludes' }
    ];
  }

  if (measurement && isEnumItem(getMeasurementItem(measurement, 'dimension', dimensionName, measurements))) {
    return Object.entries(intOperators).map(([value, label]) => ({ value, label }));
  }

  let operatorOptions: { value: string; label: string }[] = [];

  // Handle NMS
  if (checkMeasurement.isNms(measurement)) {
    const nmsOptions = dict.dictionary?.queryFilters?.nms;
    if (nmsOptions) {
      const dimensionOps = nmsOptions.operatorOptions?.[dimensionName];

      if (dimensionOps?.length) {
        operatorOptions = dimensionOps;
      } else if (nmsOptions.standardOperatorOptions?.length) {
        operatorOptions = nmsOptions.standardOperatorOptions;
      }
    }
  }

  // Handle Synth
  if (!operatorOptions.length && checkMeasurement.isSynth(measurement)) {
    if (isAgentDimension(dimensionName) || isTestDimension(dimensionName) || isContainsDimension(dimensionName)) {
      operatorOptions = dict.stringOperators ?? [];
    }
  }

  // Derive operators from the MeasurementDimension.Type field via the compiler.
  // This handles device tags (Type: 'string' → string + regex operators) and any other
  // typed dimension in the measurements catalogue without hardcoding anything here.
  if (!operatorOptions.length && measurements) {
    const measurementObj = measurements.find((m) => m.measurement === measurement);
    const dim = measurementObj?.storage.Dimensions[dimensionName];
    const operatorRecord = getOperatorsForMeasurementDimension(dim);
    if (operatorRecord) {
      operatorOptions = Object.entries(operatorRecord)
        .filter((entry): entry is [string, string] => entry[1] !== undefined)
        .map(([value, label]) => ({ value, label }));
    }
  }

  //  If nothing matches, use the static dictionary lookup & fallback
  if (!operatorOptions.length) {
    const dictionaryOps = dict.filterOperatorOptions?.[dimensionName];
    if (dictionaryOps && Array.isArray(dictionaryOps) && dictionaryOps.length > 0) {
      operatorOptions = dictionaryOps;
    } else {
      operatorOptions = dict.standardFilterOperators ?? [];
    }
  }

  return operatorOptions;
}

// Value input component that handles different dimension types
interface FilterValueInputProps {
  filter: FilterState;
  dimensionName: string;
  operator: string;
  measurement: string | null;
  measurements: Measurement[];
  $lookups: LookupStore;
  $syn?: SynStore;
  $dictionary?: Dictionary;
  $labels?: LabelStore;
  autoFocus?: boolean;
}

const FilterValueInput = observer(
  ({
    filter,
    dimensionName,
    operator,
    measurement,
    measurements,
    $lookups,
    $syn,
    $dictionary,
    $labels,
    autoFocus
  }: FilterValueInputProps) => {
    const advancedMappings = useContext(BoardVariablesContext);

    const variableOptions = useMemo<{ value: string; label: string }[]>(
      () => advancedMappings.map((m) => ({ value: `{{${m.variableName}}}`, label: m.variableName })),
      [advancedMappings]
    );

    const variableOptionRenderer = useMemo(
      () => (props: Parameters<typeof defaultOptionRenderer>[0]) => {
        if (CUSTOM_FILTER_REGEX.test(props.value)) {
          return defaultOptionRenderer({ ...props, label: <VariableTag name={props.value.slice(2, -2)} /> });
        }
        return defaultOptionRenderer(props);
      },
      []
    );

    const exactMatch = operator === '=' || operator === '<>';

    // Field comparison - show dimension selector instead of value input
    if (isFieldComparisonOperator(operator) && filter.rightFilterField) {
      const { rightFilterFieldOptions } = ($dictionary || {}) as DictionaryLike;
      const dimensionOptions = rightFilterFieldOptions?.[dimensionName] || [];

      return (
        <Field field={filter.rightFilterField} showLabel={false} mb={0} options={dimensionOptions}>
          <Select small fill autoFocus={autoFocus} menuWidth="auto" placeholder="Select dimension..." />
        </Field>
      );
    }

    // Label pseudo-dimensions (ktlabel_syn_agent, ktlabel_syn_test, i_device_label) — labelType inferred from dimensionName.
    if (isLabelDimension(dimensionName)) {
      return <LabelFilterInput filter={filter} dimensionName={dimensionName} $labels={$labels} autoFocus={autoFocus} />;
    }

    // Synthetics-specific dimension handling
    if (checkMeasurement.isSynth(measurement)) {
      if (isAgentDimension(dimensionName)) {
        return <AgentFilterInput filter={filter} operator={operator} $syn={$syn} autoFocus={autoFocus} />;
      }
      if (isTestDimension(dimensionName)) {
        return <TestFilterInput filter={filter} operator={operator} $syn={$syn} autoFocus={autoFocus} />;
      }
    }

    const dimensionItem = measurement
      ? getMeasurementItem(measurement, 'dimension', dimensionName, measurements)
      : null;
    if (isEnumItem(dimensionItem)) {
      const enumOptions = Object.entries(dimensionItem.Values).map(([value, label]) => ({ value, label }));
      return (
        <Field field={filter.filterValue} showLabel={false} mb={0} options={enumOptions} validateOptions={exactMatch}>
          <Select
            small
            fill
            autoFocus={autoFocus}
            autoComplete
            exactMatch
            showFilter
            menuWidth="auto"
            optionRenderer={variableOptionRenderer}
            placeholder="Select..."
          />
        </Field>
      );
    }

    const onQuery = $lookups.getAutoCompleteFilterHandlerWithOptions({
      fieldName: dimensionName,
      defaultOptions: variableOptions as never[]
    });

    if (onQuery) {
      return (
        <Field
          field={filter.filterValue}
          showLabel={false}
          mb={0}
          onQuery={onQuery}
          options={null}
          validateOptions={exactMatch}
        >
          <Select
            small
            fill
            autoComplete
            exactMatch={exactMatch}
            showFilter={exactMatch}
            autoFocus={autoFocus}
            menuWidth="auto"
            optionRenderer={variableOptionRenderer}
            placeholder={exactMatch ? 'Search...' : 'Enter value...'}
          />
        </Field>
      );
    }

    // Check dictionary for predefined value options first
    const { filterFieldValueOptions } = ($dictionary || {}) as DictionaryLike;
    const dictionaryValueOptions = filterFieldValueOptions?.[dimensionName];
    if (dictionaryValueOptions && Array.isArray(dictionaryValueOptions) && dictionaryValueOptions.length > 0) {
      const mergedOptions = [...variableOptions, ...dictionaryValueOptions];

      return (
        <Field field={filter.filterValue} showLabel={false} mb={0} options={mergedOptions} validateOptions={false}>
          <Select
            small
            fill
            autoFocus={autoFocus}
            menuWidth="auto"
            optionRenderer={variableOptionRenderer}
            placeholder="Select..."
          />
        </Field>
      );
    }

    if (variableOptions.length === 0) {
      return (
        <Field field={filter.filterValue} showLabel={false} mb={0}>
          <InputGroup small fill autoFocus={autoFocus} placeholder="Enter value..." />
        </Field>
      );
    }

    return (
      <Field field={filter.filterValue} showLabel={false} mb={0} options={variableOptions} validateOptions={false}>
        <Select
          small
          fill
          autoFocus={autoFocus}
          autoComplete
          showButton={false}
          exactMatch={false}
          menuWidth="auto"
          optionRenderer={variableOptionRenderer}
          placeholder="Enter value..."
        />
      </Field>
    );
  }
);

const UdeFilterItemBase = observer(
  ({
    filter,
    index,
    onRemove,
    measurement,
    measurements,
    measurementHash,
    autoFocus,
    $lookups,
    $syn,
    $dictionary,
    $labels
  }: UdeFilterItemProps) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const handleRemove = useCallback(() => {
      onRemove(index);
    }, [onRemove, index]);

    const dimensionName = filter.filterField.getValue();

    // Memoize the dimension tree structure - only recomputes when measurement context changes
    const groups = useMemo(() => {
      if (!measurement) {
        return [];
      }
      return getGroupedDimensions(measurement, measurementHash);
    }, [measurement, measurementHash]);

    // Separately memoize marking the selected dimension - only recomputes when selection changes
    const groupedDimensions = useMemo(() => markSelectedDimension(groups, dimensionName), [groups, dimensionName]);

    const dimensionLabel = useMemo(
      () => getDimensionLabel(dimensionName, groupedDimensions),
      [dimensionName, groupedDimensions]
    );

    const handleDimensionChange = useCallback(
      (value: string) => {
        filter.filterField.setValue(value);
        // Reset value when dimension changes
        filter.filterValue.setValue('');

        // Clear rightFilterField to prevent it from being used in filter generation
        if (filter.rightFilterField) {
          filter.rightFilterField.setValue('');
        }

        // Set validation rules from dictionary - but only if we're not using predefined options
        // When using filterFieldValueOptions or autocomplete, the component already constrains values
        if ($dictionary) {
          const dict = $dictionary as unknown as DictionaryLike;
          const hasPredefinedOptions = (dict.filterFieldValueOptions?.[value]?.length ?? 0) > 0;
          const hasAutocomplete = !!$lookups?.getAutoCompleteFilterHandler(value);

          if (!hasPredefinedOptions && !hasAutocomplete && dict.getFilterValueValidator) {
            const filterValidatorRules = dict.getFilterValueValidator(value);
            filter.filterValue.setRules(filterValidatorRules);
          } else {
            // Clear validation rules for dimensions with predefined options or autocomplete
            filter.filterValue.setRules('');
          }
        }

        // Smart default operator selection
        const operatorOpts = getOperatorOptions(value, measurement, $dictionary, measurements);
        const defaultOperator = getDefaultOperatorForDimension(value, operatorOpts);
        filter.operator.setValue(defaultOperator);
      },
      [filter, measurement, $dictionary, $lookups, measurements]
    );

    const handleBidirectionalSelect = useCallback(
      (keys: string[]) => {
        const srcKey = keys.find((k) => isSrcDim(k));
        const dstKey = keys.find((k) => isDstDim(k));

        if (srcKey && dstKey) {
          const biKey = buildBidirectionalKey(srcKey, dstKey);
          if (biKey) {
            handleDimensionChange(biKey);
            return;
          }
        }

        if (keys.length > 0) {
          handleDimensionChange(keys[0]);
        }
      },
      [handleDimensionChange]
    );

    const handleDirectionSelect = useCallback(
      (value: string) => {
        const current = dimensionName;

        if (current === value) {
          handleDimensionChange('');
          return;
        }

        const biParts = current ? splitBidirectionalKey(current) : null;
        if (biParts) {
          if (biParts.srcVariant === value) {
            handleDimensionChange(biParts.dstVariant);
          } else if (biParts.dstVariant === value) {
            handleDimensionChange(biParts.srcVariant);
          } else {
            handleDimensionChange(value);
          }
          return;
        }

        if (current) {
          let biKey: string | null = null;
          if (isSrcDim(current) && isDstDim(value)) {
            biKey = buildBidirectionalKey(current, value);
          } else if (isDstDim(current) && isSrcDim(value)) {
            biKey = buildBidirectionalKey(value, current);
          }
          if (biKey) {
            handleDimensionChange(biKey);
            return;
          }
        }
        handleDimensionChange(value);
      },
      [dimensionName, handleDimensionChange]
    );

    const handleOperatorChange = useCallback(
      (field: unknown, value: string) => {
        const isFieldComparison = isFieldComparisonOperator(value);

        if (isFieldComparison) {
          // Switching TO field comparison mode - clear regular value
          filter.filterValue.setValue('');
        } else if (filter.rightFilterField) {
          // Switching FROM field comparison mode - clear right field
          filter.rightFilterField.setValue('');
        }
      },
      [filter]
    );

    // Get operator options based on dimension type
    const operatorOptions = useMemo(
      () => getOperatorOptions(dimensionName, measurement, $dictionary, measurements),
      [dimensionName, measurement, $dictionary, measurements]
    );

    const currentOperator = filter.operator.getValue();

    // Check if value field should be shown based on operator
    const dictLike = ($dictionary || {}) as DictionaryLike;
    const suppressValueOperators = dictLike.dictionary?.queryFilters?.suppressValueOperators || [];
    const isFieldComparison = isFieldComparisonOperator(currentOperator);
    const showValueField = !suppressValueOperators.includes(currentOperator) && !isFieldComparison;
    const showRightField = isFieldComparison && !!filter.rightFilterField;

    return (
      <Flex alignItems="flex-start" gap={1} p={1} borderBottom="thin">
        <Box flex="1" minWidth={0} position="relative">
          <DropdownMenu
            isOpen={isDropdownOpen}
            onInteraction={(nextOpenState) => setIsDropdownOpen(nextOpenState)}
            position={Position.BOTTOM_LEFT}
            boundary="viewport"
            usePortal
            canEscapeKeyClose
            content={
              <DimensionMenu
                measurement={measurement}
                measurementHash={measurementHash}
                groupedDimensions={groupedDimensions}
                onSelect={handleDirectionSelect}
                onSelectBoth={handleBidirectionalSelect}
                dismissOnSelect
              />
            }
          >
            <Button
              text={dimensionLabel || <Text muted>Select dimension...</Text>}
              small
              fill
              textAlign="left"
              rightIcon="caret-down"
              justifyContent="space-between"
            />
          </DropdownMenu>
        </Box>

        <Box flex={showValueField || showRightField ? '0 0 155px' : '0 0 185px'}>
          <Field
            field={filter.operator}
            showLabel={false}
            mb={0}
            options={operatorOptions}
            onChange={handleOperatorChange}
          >
            <Select small fill />
          </Field>
        </Box>

        <Box flex={1} minWidth={0}>
          {(showValueField || showRightField) && (
            <FilterValueInput
              filter={filter}
              dimensionName={dimensionName}
              operator={currentOperator}
              measurement={measurement}
              measurements={measurements}
              $lookups={$lookups as LookupStore}
              $syn={$syn}
              $dictionary={$dictionary}
              $labels={$labels}
              autoFocus={autoFocus}
            />
          )}
        </Box>

        <Tooltip content="Remove filter" placement="top">
          <Button icon="small-cross" minimal small intent="danger" onClick={handleRemove} />
        </Tooltip>
      </Flex>
    );
  }
);

const UdeFilterItem = inject('$lookups', '$syn', '$dictionary', '$labels')(UdeFilterItemBase);

export default UdeFilterItem;
